import { ChevronDown } from 'lucide-react';
import type { HomeCategoryItem } from '../../types/homeCategory';

type HomeCategoryNavProps = {
  variant: 'sidebar' | 'pills' | 'mobile-strip';
  categories: HomeCategoryItem[];
  loading: boolean;
  activeId: number | null;
  onSelect: (id: number) => void;
  className?: string;
};

function CategoryThumb({
  category,
  active,
  size = 'sm',
}: {
  category: HomeCategoryItem;
  active: boolean;
  size?: 'sm' | 'md';
}) {
  const Icon = category.icon;
  const box = size === 'md' ? 'h-12 w-12' : 'h-8 w-8';
  const icon = size === 'md' ? 'h-5 w-5' : 'h-4 w-4';

  if (category.image) {
    return (
      <img
        src={category.image}
        alt=""
        className={`${box} shrink-0 rounded-full object-cover ring-1 ring-neutral-200`}
      />
    );
  }

  return (
    <div
      className={`flex ${box} shrink-0 items-center justify-center rounded-full ${
        active ? 'bg-micron-red/10' : 'bg-neutral-100'
      }`}
    >
      <Icon className={`${icon} ${active ? 'text-micron-red' : 'text-neutral-500'}`} />
    </div>
  );
}

export default function HomeCategoryNav({
  variant,
  categories,
  loading,
  activeId,
  onSelect,
  className = '',
}: HomeCategoryNavProps) {
  if (loading) {
    if (variant === 'sidebar') {
      return (
        <aside
          className={`hidden rounded-lg border border-neutral-200 bg-white lg:block ${className}`}
        >
          <ul className="space-y-1 p-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i} className="h-10 animate-pulse rounded-lg bg-neutral-100" />
            ))}
          </ul>
        </aside>
      );
    }
    return (
      <div className={`flex gap-3 overflow-x-auto pb-2 ${className}`}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-20 w-20 shrink-0 animate-pulse rounded-2xl bg-neutral-200" />
        ))}
      </div>
    );
  }

  if (categories.length === 0) {
    return null;
  }

  if (variant === 'sidebar') {
    return (
      <aside
        className={`hidden rounded-lg border border-neutral-200 bg-white lg:block ${className}`}
      >
        <ul className="py-1">
          {categories.map((category) => {
            const active = activeId === category.id;
            const Icon = category.icon;
            return (
              <li key={category.id}>
                <button
                  type="button"
                  onClick={() => onSelect(category.id)}
                  className={`flex w-full items-center gap-3 border-l-2 px-4 py-2.5 text-left text-sm transition ${
                    active
                      ? 'border-micron-red bg-red-50 font-semibold text-micron-black'
                      : 'border-transparent text-neutral-600 hover:bg-neutral-50'
                  }`}
                >
                  {category.image ? (
                    <img
                      src={category.image}
                      alt=""
                      className="h-8 w-8 shrink-0 rounded-md object-cover"
                    />
                  ) : (
                    <Icon
                      className={`h-4 w-4 shrink-0 ${active ? 'text-micron-red' : 'text-neutral-400'}`}
                    />
                  )}
                  <span className="min-w-0 flex-1 truncate">{category.name}</span>
                  {category.products_count != null && category.products_count > 0 && (
                    <span className="text-[10px] font-medium text-neutral-400">
                      {category.products_count}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </aside>
    );
  }

  const isPills = variant === 'pills';

  return (
    <div
      className={`flex gap-4 overflow-x-auto pb-2 ${isPills ? '' : 'lg:hidden'} ${className}`}
    >
      {categories.map((category) => {
        const active = activeId === category.id;
        const Icon = category.icon;
        return (
          <button
            key={category.id}
            type="button"
            onClick={() => onSelect(category.id)}
            className={`flex shrink-0 items-center gap-2 rounded-2xl transition ${
              isPills
                ? `flex-col px-4 py-3 ${
                    active
                      ? 'bg-micron-red text-white shadow-lg shadow-red-200'
                      : 'bg-neutral-50 text-neutral-600 hover:bg-red-50 hover:text-micron-red'
                  }`
                : `border px-3 py-2 ${
                    active
                      ? 'border-micron-red bg-red-50 text-micron-black'
                      : 'border-neutral-200 bg-white text-neutral-600'
                  }`
            }`}
          >
            {isPills ? (
              <>
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-full ${
                    active ? 'bg-white/20' : 'bg-white shadow-sm'
                  }`}
                >
                  {category.image ? (
                    <img src={category.image} alt="" className="h-full w-full rounded-full object-cover" />
                  ) : (
                    <Icon className={`h-5 w-5 ${active ? 'text-white' : 'text-micron-red'}`} />
                  )}
                </div>
                <span className="max-w-[88px] truncate text-xs font-semibold">{category.name}</span>
              </>
            ) : (
              <>
                <CategoryThumb category={category} active={active} />
                <span className="max-w-[120px] truncate text-sm font-medium">{category.name}</span>
                <ChevronDown className="hidden h-3.5 w-3.5 text-neutral-400 sm:block" />
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
